/**
 * /knowledge-base/categories — Hierarchical category CRUD (Milestone 1).
 * Manager+ only.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Card, CardHeader, CardTitle, CardContent,
} from "@/components/ui/card";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { ArrowLeft, Plus, Folder, Edit, Trash2, FolderTree } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", parent_id: "", icon: "", color: "", sort_order: 0 };

export default function KnowledgeBaseCategories() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const { activeSlug } = useClient();

  const load = () => api.get("/kb-categories").then((r) => setRows(r.data || []));
  useEffect(() => { load(); }, [activeSlug]);   // eslint-disable-line

  const tree = useMemo(() => labelTree(rows), [rows]);

  const openCreate = () => { setEditingId(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({
      name: row.name, parent_id: row.parent_id || "",
      icon: row.icon || "", color: row.color || "",
      sort_order: row.sort_order || 0,
    });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    const payload = { ...form, parent_id: form.parent_id || null };
    try {
      if (editingId) await api.patch(`/kb-categories/${editingId}`, payload);
      else await api.post("/kb-categories", payload);
      toast.success(editingId ? "Updated" : "Created");
      setOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete category "${row.name}"? (Only allowed when empty.)`)) return;
    try {
      await api.delete(`/kb-categories/${row.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-4 max-w-3xl mx-auto" data-testid="kb-categories-page">
      <Link to="/knowledge-base" className="inline-flex items-center text-sm text-slate-500 hover:text-slate-800">
        <ArrowLeft className="w-4 h-4 mr-1" /> Knowledge Base
      </Link>

      <div className="flex items-center justify-between">
        <h1 className="font-heading text-xl font-semibold text-slate-900 flex items-center gap-2">
          <FolderTree className="w-5 h-5 text-indigo-600" /> Categories
        </h1>
        <Button onClick={openCreate} className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="kb-cat-new-btn">
          <Plus className="w-3.5 h-3.5 mr-1.5" /> New category
        </Button>
      </div>

      <Card className="border-slate-200">
        <CardHeader className="pb-2"><CardTitle className="font-heading text-sm uppercase tracking-wider text-slate-500">Tree</CardTitle></CardHeader>
        <CardContent className="-mt-1 space-y-0.5">
          {tree.length === 0 && (
            <div className="text-sm text-slate-400 py-6 text-center">
              No categories yet. <button type="button" onClick={openCreate} className="text-indigo-600 hover:text-indigo-800 underline">Create the first one</button>.
            </div>
          )}
          {tree.map((r) => (
            <div key={r.id} className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-slate-50"
                 style={{ paddingLeft: `${8 + r.depth * 16}px` }}
                 data-testid={`kb-cat-row-${r.id}`}>
              <Folder className="w-4 h-4 text-slate-400" />
              <span className="flex-1 text-sm text-slate-800">{r.name}</span>
              <span className="text-[10px] text-slate-400">{r.article_count || 0} articles</span>
              <Button variant="ghost" size="sm" onClick={() => openEdit(r)} className="h-7 w-7 p-0"
                      data-testid={`kb-cat-edit-${r.id}`}><Edit className="w-3.5 h-3.5" /></Button>
              <Button variant="ghost" size="sm" onClick={() => remove(r)}
                      className="h-7 w-7 p-0 text-rose-500 hover:text-rose-700"
                      data-testid={`kb-cat-delete-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
          ))}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="kb-cat-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editingId ? "Edit category" : "New category"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                     placeholder="Online Shopping" data-testid="kb-cat-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Parent (optional)</Label>
              <Select value={form.parent_id || "__none"} onValueChange={(v) => setForm({ ...form, parent_id: v === "__none" ? "" : v })}>
                <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none" className="text-xs">— None (top level) —</SelectItem>
                  {tree.filter((t) => t.id !== editingId).map((t) => (
                    <SelectItem key={t.id} value={t.id} className="text-xs">
                      {"  ".repeat(t.depth)}{t.depth > 0 ? "└ " : ""}{t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Sort order</Label>
              <Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value || "0", 10) })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} className="bg-indigo-600 hover:bg-indigo-700"
                    data-testid="kb-cat-submit-btn">
              {editingId ? "Save" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function labelTree(rows) {
  const byId = Object.fromEntries(rows.map((r) => [r.id, r]));
  const out = [];
  const visit = (id, depth) => {
    const r = byId[id]; if (!r) return;
    out.push({ ...r, depth });
    rows.filter((x) => x.parent_id === id)
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
      .forEach((c) => visit(c.id, depth + 1));
  };
  rows.filter((r) => !r.parent_id || !byId[r.parent_id])
    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
    .forEach((r) => visit(r.id, 0));
  return out;
}
